import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Button,
  TextField,
  DialogContentText,
} from "@mui/material";
import { styled } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { buttonStyle, themeStyles } from "../../../constants";
import {
  DatePicker,
  TimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useSnackbar } from "../../../Providers/SnackbarProvider";
import axios from "axios";
import { xorDecrypt } from "../../../encrypt";
import { BASE_API_URL } from "../../../constants";
import axiosInstance from "../../../api/axiosInstance";

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiFilledInput-root": {
    backgroundColor: "#f5f5f5", // Default background color
    borderRadius: "8px",
  },
  "& .MuiFilledInput-root.Mui-disabled": {
    backgroundColor: "#f5f5f5", // Maintain background when disabled
  },
  "& .MuiInputBase-input.Mui-disabled": {
    color: "#ff9800", // Change only the value text color when disabled
  },
  "& .MuiInputLabel-root.Mui-disabled": {
    color: "#999", // Keep a subtle gray for disabled labels
  },
}));

const SessionNoteContent = ({
  open,
  handleClose,
  selectedRow,
  clients,
  onUpdate,
}) => {
  const { openSnackbar } = useSnackbar();
  const [formValues, setFormValues] = useState({ ...selectedRow });
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const sharedLink = BASE_API_URL;

  const getClientName = (clientId) => {
    const client = clients.find((client) => client.id === clientId);
    return client ? client.clientName : "N/A";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const convertArrayToSemicolonString = (array) => {
    return Array.isArray(array) ? array.join(";") : array;
  };

  const handleSave = async () => {
    setConfirmSaveOpen(false);
    try {
      const subscriberId = xorDecrypt(localStorage.getItem("subscriberId"));
      let noteContent = formValues.noteContent;
      noteContent = noteContent.replaceAll(/\\"\\\[/g, "(");
      noteContent = noteContent.replaceAll(/\\]\\"/g, "]");
      noteContent = noteContent.replaceAll(/\\"/g, "\"");
      noteContent = noteContent.replace(/\n/g, " ");
      noteContent = noteContent.replace(/"/g, ""); // Remove any double quotes
  
      const convertArrayToIds = (array) => {
        return Array.isArray(array) ? array.map(item => item.id) : [];
      };
  
      const payload = {
        sessionId: formValues.sessionId,
        clientId: formValues.clientId,
        clientDateOfBirth: formValues.clientDateOfBirth,
        subscriberId,
        sessionType: formValues.sessionType,
        procedureCode: formValues.procedureCode,
        noteContent,
        sessionDate: formValues.sessionDate,
        sessionParticipants: convertArrayToSemicolonString(formValues.sessionParticipants),
        otherSessionParticipants: formValues.otherSessionParticipants,
        rbtWorkStyle: convertArrayToSemicolonString(formValues.rbtWorkStyle),
        otherRbtWorkStyle: formValues.otherRbtWorkStyle,
        behaviorStrategiesApplied: convertArrayToSemicolonString(formValues.behaviorStrategiesApplied),
        otherSkillStrategiesApplied: formValues.otherSkillStrategiesApplied,
        otherBehaviorStrategiesApplied: formValues.otherBehaviorStrategiesApplied,
        sessionStartTime: formValues.sessionStartTime,
        sessionEndTime: formValues.sessionEndTime,
        malAdaptiveBehaviorExists: formValues.malAdaptiveBehaviorExists,
        malAdaptiveBehaviors: convertArrayToIds(formValues.malAdaptiveBehaviors),
        modifier: formValues.modifier,
        reinforcerUsed: formValues.reinforcerUsed,
        diagnosisCode1: formValues.diagnosisCode1,
        placeOfService: convertArrayToSemicolonString(formValues.placeOfService),
        reinforcers: convertArrayToIds(formValues.reinforcers),
        skillStrategyUsed: formValues.skillStrategyUsed,
        behaviorInterventionUsed: formValues.behaviorInterventionUsed,
        primaryTargetAreasAddressed: convertArrayToIds(formValues.primaryTargetAreasAddressed),
        clientMoodResponse: convertArrayToIds(formValues.clientMoodResponse),
        nextSessionPlans: convertArrayToIds(formValues.nextSessionPlans),
        treatmentPlanFollowed: formValues.treatmentPlanFollowed,
        parentOrGurdianName: formValues.parentOrGurdianName,
        sessionStatus: formValues.sessionStatus,
      };
  
      //console.log("payload of updated", JSON.stringify(payload));
  
      const response = await axiosInstance.post(
        `${sharedLink}/updatesessionnote`,
        payload,
        {}
      );
  
      if (response.status === 200) {
        openSnackbar("Session note updated successfully!", "success");
        onUpdate(formValues);
        handleClose();
      } else {
        openSnackbar("Failed to update session note.", "error");
      }
    } catch (error) {
      openSnackbar("An error occurred. Please try again.", "error");
    }
  };

  const handleCancel = () => {
    setConfirmCancelOpen(false);
    setFormValues({ ...selectedRow });
    handleClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ backgroundColor: "#000", color: "#fff" }}>
          Session Note [ Boxes which are highlighed are not editable]
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#fff",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#000", color: "#fff" }}>
          <Box
            display="grid"
            gap="20px"
            gridTemplateColumns="repeat(2, 1fr)"
            sx={{
              "& .MuiTextField-root": {
                backgroundColor: "#333",
                color: "#fff",
                borderRadius: "8px",
                "& .MuiInputBase-input": {
                  color: "#fff",
                },
                "& .MuiInputLabel-root": {
                  color: "#fff",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#fff",
                },
                "& .MuiSvgIcon-root": {
                  color: "#fff",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#555",
                  color: "#bbb",
                },
              },
            }}
          >
            <StyledTextField
              label="Client Name"
              value={getClientName(formValues.clientId)}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              InputLabelProps={{ style: { color: "#4d4d4d" } }}
              fullWidth
              variant="filled"
              disabled
            />
            <StyledTextField
              label="Procedure Code"
              value={formValues.procedureCode}
              InputProps={{
                readOnly: true,
                disableUnderline: true,
              }}
              InputLabelProps={{ style: { color: "#4d4d4d" } }}
              fullWidth
              variant="filled"
              disabled
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Session Date"
                value={dayjs(formValues.sessionDate)}
                onChange={(newValue) => {
                  setFormValues({
                    ...formValues,
                    sessionDate: newValue.format("YYYY-MM-DD"),
                  });
                }}
                maxDate={dayjs()} // Restricts future dates
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="filled"
                    fullWidth
                    sx={{ borderRadius: "8px" }}
                  />
                )}
              />
              <TextField
                label="Diagnosis Code"
                name="diagnosisCode1"
                value={formValues.diagnosisCode1}
                onChange={handleInputChange}
                fullWidth
                variant="filled"
                sx={{ borderRadius: "8px" }}
              />
              <TimePicker
                label="Session Start Time"
                value={dayjs(formValues.sessionStartTime, "HH:mm")}
                onChange={(newValue) => {
                  setFormValues({
                    ...formValues,
                    sessionStartTime: newValue ? newValue.format("HH:mm") : "",
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="filled"
                    fullWidth
                    sx={{ borderRadius: "8px" }}
                  />
                )}
              />
              <TimePicker
                label="Session End Time"
                value={dayjs(formValues.sessionEndTime, "HH:mm")}
                onChange={(newValue) => {
                  if (
                    newValue &&
                    newValue.isBefore(
                      dayjs(formValues.sessionStartTime, "HH:mm")
                    )
                  ) {
                    // Show warning if end time is less than start time
                    openSnackbar(
                      "Session end time cannot be earlier than start time.",
                      "error"
                    );
                    return; // Prevent updating state
                  }
                  setFormValues({
                    ...formValues,
                    sessionEndTime: newValue ? newValue.format("HH:mm") : "",
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="filled"
                    fullWidth
                    sx={{ borderRadius: "8px" }}
                  />
                )}
              />
            </LocalizationProvider>
            <TextField
              label="Modifier"
              name="modifier"
              value={formValues.modifier}
              onChange={handleInputChange}
              fullWidth
              variant="filled"
              sx={{ borderRadius: "8px" }}
            />
            <TextField
              label="Place of Service"
              name="placeOfService"
              value={formValues.placeOfService}
              onChange={handleInputChange}
              fullWidth
              variant="filled"
              sx={{ borderRadius: "8px" }}
            />
            <TextField
              label="Session Participants"
              name="sessionParticipants"
              value={formValues.sessionParticipants}
              onChange={handleInputChange}
              fullWidth
              variant="filled"
              sx={{ borderRadius: "8px" }}
            />
            <TextField
              label="Note Content"
              name="noteContent"
              value={formValues.noteContent}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={6}
              variant="filled"
              sx={{ gridColumn: "span 2", borderRadius: "8px" }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#000", color: "#fff" }}>
          <Button
            onClick={() => setConfirmCancelOpen(true)}
            color="secondary"
            sx={{ ...buttonStyle}}
            variant="contained"
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button
            onClick={() => setConfirmSaveOpen(true)}
            color="primary"
            variant="contained"
            sx={{ ...buttonStyle }}
            startIcon={<SaveIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Cancel Dialog */}
      <Dialog
        open={confirmCancelOpen}
        onClose={() => setConfirmCancelOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#333",
            color: "white",
          },
        }}
      >
        <DialogTitle>{"Confirm Cancel"}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "white" }}>
            Are you sure you want to cancel?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmCancelOpen(false)}
            sx={{
              color: "white",
              "&:hover": { backgroundColor: "#444" },
            }}
          >
            No
          </Button>
          <Button
            onClick={handleCancel}
            sx={{
              color: "white",
              backgroundColor: "#e9a132",
              "&:hover": { backgroundColor: "#d0892f" },
            }}
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Save Dialog */}
      <Dialog
        open={confirmSaveOpen}
        onClose={() => setConfirmSaveOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#333",
            color: "white",
          },
        }}
      >
        <DialogTitle>{"Confirm Save"}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "white" }}>
            Are you sure you want to save the changes?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmSaveOpen(false)}
            sx={{
              color: "white",
              "&:hover": { backgroundColor: "#444" },
            }}
          >
            No
          </Button>
          <Button
            onClick={handleSave}
            sx={{
              color: "white",
              backgroundColor: "#e9a132",
              "&:hover": { backgroundColor: "#d0892f" },
            }}
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SessionNoteContent;